import { MICRO_USD_PER_CREDIT } from '../constants'

import { createTranslator, type useTranslations } from 'next-intl'
import english from '@/translations/en/credit-format.json'

const englishTranslator = createTranslator({locale: 'en', messages: english, namespace: 'CreditFormat'})

export interface CreditPresentation {
  t: ReturnType<typeof useTranslations<'CreditFormat'>>
  numbers: Intl.NumberFormat
}

const englishPresentation: CreditPresentation = {
  t: englishTranslator,
  numbers: new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}),
}

/** Whole grouped credits; a fraction rounds away from zero, so small charges never show as 0. */
export function formatCredits(microUsd: number, presentation: CreditPresentation = englishPresentation): string {
  const credits = roundCreditsUp(microUsd)
  return presentation.t("credits", {count: Math.abs(credits), amount: presentation.numbers.format(credits)})
}

/** Compact form for dense usage lines. */
export function formatCreditsShort(microUsd: number, presentation: CreditPresentation = englishPresentation): string {
  return presentation.t("short", {amount: presentation.numbers.format(roundCreditsUp(microUsd))})
}

/** "500 credits expire on {date}", the verb agreeing with the displayed count. */
export function formatCreditsExpiring(microUsd: number, date: string, presentation: CreditPresentation = englishPresentation): string {
  return presentation.t("expiring", {count: Math.abs(roundCreditsUp(microUsd)), credits: formatCredits(microUsd, presentation), date})
}

export function formatCreditsSigned(microUsd: number, presentation: CreditPresentation = englishPresentation): string {
  const prefix = microUsd >= 0 ? '+' : ''
  return `${prefix}${formatCredits(microUsd, presentation)}`
}

/** Exact value for editable inputs: saving a form must not apply display rounding. */
export function creditsInputValue(microUsd: number): string {
  return String(microUsd / MICRO_USD_PER_CREDIT)
}

/** Empty/invalid values stay distinct from zero; storage precision is one microUSD. */
export function parseCreditsToMicroUsd(credits: string | number): number | null {
  if (typeof credits === 'string' && credits.trim() === '') return null
  const amount = Number(credits)
  const microUsd = Math.round(amount * MICRO_USD_PER_CREDIT)
  return Number.isSafeInteger(microUsd) ? microUsd : null
}

function roundCreditsUp(microUsd: number): number {
  const magnitude = Math.ceil(Math.abs(microUsd) / MICRO_USD_PER_CREDIT)
  return microUsd < 0 ? -magnitude : magnitude
}
